import { Track } from "@/src/lib/tracking";
import { useCallback, useContext, useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import { useSnapshot } from "valtio";
import { useQueryClient } from "@tanstack/react-query";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { rootStore } from "@store/rootStore";
import { invalidateChatDetailQueries } from "../utils/chatDetailInvalidateQueries";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";
import Translations from "@translate/hooks/useChatUpdate";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);
interface UpdateChatParams {
  id: string;
  title?: string;
  mark?: 0 | 1;
}

type IUpdateChatParams = {
  params: UpdateChatParams;
  callback?: () => void;
};

export const useChatUpdate = () => {
  const { chatApi } = useContext(OspApiClientContext);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const updateChat = useCallback(
    async (params: UpdateChatParams) => {
      if (!chatApi) {
        return false;
      }
      setIsLoading(true);
      try {
        const { id, ...updateData } = params;
        await chatApi?.chats.chatsUpdate(id, updateData as any);
        invalidateChatDetailQueries(queryClient, id, { includeHistory: true });
        return true;
      } catch (error) {
        Track.log("error", "useChatUpdate", "Failed to update chat:", {
          cause: String(error),
        });
        OspToast.error({
          content: I18n.t("Failed to update chat"),
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [chatApi, queryClient],
  );

  const handleUpdateChat = async (data: IUpdateChatParams) => {
    const { callback, params } = data || {};
    if (!isLoggedIn) {
      callback?.();
      redirectToLoginPage();
      return;
    }
    return await updateChat(params);
  };

  const deleteChat = useCallback(
    async (id: string) => {
      if (!chatApi) {
        return false;
      }
      setIsLoading(true);
      try {
        await chatApi?.chats.chatsDelete(id);
        invalidateChatDetailQueries(queryClient, id, { includeHistory: true });
      } catch (error) {
        Track.log("error", "useChatUpdate", "Failed to delete chat:", {
          cause: String(error),
        });
        OspToast.error({
          content: I18n.t("Failed to delete chat"),
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [chatApi, queryClient],
  );

  return {
    updateChat,
    handleUpdateChat,
    deleteChat,
    isLoading,
  };
};
